import { motion } from "framer-motion";
import { TextField, Container, Typography, Alert } from "@mui/material";
import catImage from "../images/homecat.png";
import dogImage from "../images/homeimg.png";
import Navbar from "./Navbar.js";
import React, { useState } from "react";
import axiosInstance from "../api/axios";

const textVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 1 } },
};

const buttonVariants = {
  hover: { backgroundColor: "#BDA697", transition: { duration: 0.3 } },
};

const imageVariants = {
  hidden: { opacity: 0, x: -100 },
  visible: { opacity: 1, x: 0, transition: { duration: 1 } },
};

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(null); // null | 'sending' | 'success' | 'error'

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus("sending");

    try {
      await axiosInstance.post("/forgot-password", { email });
      setStatus("success");
    } catch (err) {
      console.error("ForgotPassword error:", err);
      if (err.response?.status >= 500) {
        setStatus("error");
      } else {
        // Always show success — don't reveal whether account exists
        setStatus("success");
      }
    }
  };

  return (
    <section className="relative text-center mt-20 pt-16">
      <Navbar />
      <Container maxWidth="sm" className="mt-10">
        <motion.h1
          variants={textVariants}
          initial="hidden"
          animate="visible"
          className="text-5xl lg:text-[60px] lg:leading-[70px] font-bold text-gray-900"
        >
          Forgot Password
        </motion.h1>

        <motion.p
          variants={textVariants}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.2 }}
          className="mt-4 text-xl lg:text-[18px] lg:leading-[28px] poppins-text text-gray-600"
        >
          Enter your email address and we'll send you a link to reset your
          password.
        </motion.p>

        {status === "success" && (
          <Alert severity="success" className="mt-4 mb-2">
            If an account with that email exists, a password reset link has been
            sent.
          </Alert>
        )}
        {status === "error" && (
          <Alert severity="error" className="mt-4 mb-2">
            Something went wrong. Please try again later.
          </Alert>
        )}
        {status === "sending" && (
          <Alert severity="info" className="mt-4 mb-2">
            Sending reset link...
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="mt-6 text-left">
          <motion.div
            variants={textVariants}
            initial="hidden"
            animate="visible"
            transition={{ delay: 0.6 }}
            className="mb-4"
          >
            <Typography variant="h6" className="mb-2 text-left">
              Email Address
            </Typography>
            <TextField
              placeholder="Email"
              type="email"
              variant="outlined"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "1rem",
                  backgroundColor: "#f5f5f5",
                  "& fieldset": { borderColor: "transparent" },
                  "&:hover fieldset": { borderColor: "#cbd5e1" },
                },
              }}
            />
          </motion.div>

          <motion.div className="mt-6 flex justify-center">
            <motion.button
              variants={buttonVariants}
              whileHover="hover"
              disabled={status === "sending"}
              className="mt-6 text-xl lg:text-[20px] lg:leading-[26px] px-6 py-3 bg-[#967d6c] text-white font-bold rounded-full w-full disabled:opacity-60"
            >
              {status === "sending" ? "Sending..." : "Send Reset Link"}
            </motion.button>
          </motion.div>
        </form>

        <motion.p
          variants={textVariants}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.8 }}
          className="mt-6 text-lg lg:text-[18px] lg:leading-[28px] poppins-text text-gray-600"
        >
          Remember your password?
          <motion.a
            href="/login"
            className="text-[#967d6c] hover:underline ml-2 transition duration-300"
            whileHover={{ scale: 1.1, transition: { duration: 0.3 } }}
          >
            Login
          </motion.a>
        </motion.p>
      </Container>

      <motion.div
        className="absolute left-0 top-20 hidden xl:block"
        variants={imageVariants}
        initial="hidden"
        animate="visible"
      >
        <img src={catImage} alt="Cat" className="h-[36rem] mx-auto" />
        <div className="absolute left-60 bottom-20 transform -translate-x-1/2 hidden sm:block">
          <div className="text-6xl text-[#bda697] animate-paw-left-right">
            <i className="fa fa-paw"></i>
          </div>
        </div>
        <div className="absolute left-64 top-80 transform translate-x-1/2 hidden xl:block">
          <div className="text-4xl text-[#bda697] animate-paw-left-right">
            <i className="fa fa-paw"></i>
          </div>
        </div>
      </motion.div>

      <motion.div
        className="absolute right-0 top-24 hidden xl:block"
        variants={{ ...imageVariants, hidden: { opacity: 0, x: 100 } }}
        initial="hidden"
        animate="visible"
      >
        <img src={dogImage} alt="Dog" className="h-[34rem] mx-auto" />
        <div className="absolute right-24 bottom-2 transform -translate-x-1/2 hidden xl:block">
          <div className="text-4xl text-[#bda697] animate-paw-left-right">
            <i className="fa fa-paw"></i>
          </div>
        </div>
        <div className="absolute right-64 bottom-16 transform translate-x-1/2 hidden sm:block">
          <div className="text-6xl text-[#bda697] animate-paw-left-right">
            <i className="fa fa-paw"></i>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default ForgotPassword;
